/**
 * Raw API functions for product data.
 * No React, no hooks — pure async functions safe to use anywhere.
 */
import axiosObj from '@/store/axios';
import { prepareErrorObj } from '@/lib/utils/errorUtils';
import {
  ProductByIdsParams,
  ProductDetailParams,
  ProductQueryParams,
  FilterParams,
} from '@/lib/schemas/product';

export const getProductsByIds = async (config: ProductByIdsParams): Promise<any> => {
  if (config.productIds.length === 0) return;
  let url = `/api/products?productIds=${config.productIds.join(',')}`;
  if (config.brand && config.brand !== 'nestingale') {
    url += `&vendor=${config.brand}`;
  }
  if (config.env && config.env !== 'prod') {
    url += (url.includes('?') ? '&env=' : '?env=') + config.env;
  }
  console.log('productApi: getProductsByIds url=', url);
  const response = await axiosObj
    .get(url, { headers: { 'Content-Type': 'application/json' } })
    .catch((err: unknown) => {
      throw Object.assign(
        { description: 'NEST_SPA:Api:getProductsByIds:Exception', errorCode: 'NEST_SPA:PRODUCTS_PAGE' },
        prepareErrorObj(err),
      );
    });
  return response.data;
};

export const getProductsData = async (config: ProductQueryParams): Promise<any> => {
  let url = '/api/products?';
  if (config.productTypeName) url += `&productType=${config.productTypeName}`;
  if (config.filterBy) url += `&filters=${config.filterBy}`;
  const sortBy = config.sortBy || 'created_at';
  url += `&sortBy=${sortBy}`;
  if (config.brand && config.brand !== 'nestingale') url += `&vendor=${config.brand}`;
  if (config.env && config.env !== 'prod') url += `&env=${config.env}`;
  // userType is stored in the session cookie but NOT sent to BE yet (pending BE support)
  // if (config.userType) url += `&userType=${config.userType}`;
  console.log('productApi: getProductsData url=', url, 'config=', JSON.stringify(config));
  const response = await axiosObj.get(url).catch((err: unknown) => {
    console.error('productApi: getProductsData FETCH ERROR: URL: ', url,  err);
    throw Object.assign(
      { description: 'NEST_SPA:Api:getProductsData:Exception', errorCode: 'NEST_SPA:PRODUCTS_PAGE' },
      prepareErrorObj(err),
    );
  });
  console.log('productApi: getProductsData RESPONSE status=', response.status, 'productsCount=', response.data?.products?.length ?? 0);
  return response.data;
};

export const getMainMenuFiltersData = async (config: FilterParams): Promise<any> => {
  let url = `/api/filters?productType=${config.productTypeName}`;
  if (config.brand && config.brand !== 'nestingale') url += `&vendor=${config.brand}`;
  if (config.env && config.env !== 'prod') url += `&env=${config.env}`;
  console.log('productApi: getMainMenuFiltersData url=', url);
  const response = await axiosObj.get(url).catch((err: unknown) => {
    throw Object.assign(
      { description: 'NEST_SPA:Api:getMainMenuFiltersData:Exception', errorCode: 'NEST_SPA:PRODUCTS_PAGE' },
      prepareErrorObj(err),
    );
  });
  return response.data;
};

export const getSubTypeFiltersData = async (config: FilterParams): Promise<any> => {
  let url = `/api/filterValues?productType=${config.productTypeName}&dataType=productSubType`;
  if (config.mainMenuFilterType && config.mainMenuFilterName) {
    url += `&filters=${config.mainMenuFilterType}:${config.mainMenuFilterName}`;
  }
  if (config.brand && config.brand !== 'nestingale') url += `&vendor=${config.brand}`;
  if (config.env && config.env !== 'prod') url += `&env=${config.env}`;
  const response = await axiosObj.get(url).catch((err: unknown) => {
    throw Object.assign(
      { description: 'NEST_SPA:Api:getSubTypeFiltersData:Exception', errorCode: 'NEST_SPA:PRODUCTS_PAGE' },
      prepareErrorObj(err),
    );
  });
  return response.data;
};

export const getSearchProductsData = async (config: ProductQueryParams): Promise<any> => {
  let url = '/api/search?';
  if (config.productTypeName) url += `searchInput=${config.productTypeName}`;
  if (config.filterBy) url += `&filters=${config.filterBy}`;
  if (config.sortBy) url += `&sortBy=${config.sortBy}`;
  if (config.brand && config.brand !== 'nestingale') url += `&vendor=${config.brand}`;
  if (config.env && config.env !== 'prod') url += `&env=${config.env}`;
  const response = await axiosObj.get(url).catch((err: unknown) => {
    throw Object.assign(
      { description: 'NEST_SPA:Api:getSearchProductsData:Exception', errorCode: 'NEST_SPA:SEARCH_PAGE' },
      prepareErrorObj(err),
    );
  });
  return response.data;
};

export const getProductData = async (config: ProductDetailParams): Promise<any> => {
  let url = `/api/productDetails?productId=${config.productId}&includeRecommendations=true`;
  if (config.brand && config.brand !== 'nestingale') url += `&vendor=${config.brand}`;
  if (config.env && config.env !== 'prod') url += `&env=${config.env}`;
  const response = await axiosObj.get(url).catch((err: unknown) => {
    throw Object.assign(
      { description: 'NEST_SPA:Api:getProductData:Exception', errorCode: 'NEST_SPA:PDP_PAGE' },
      prepareErrorObj(err),
    );
  });

  const data = response.data;

  // Filter similar/related product recommendations to current brand only
  if (config.brand) {
    const brandMatch = (p: any) => !p.vendorName || p.vendorName === config.brand;
    if (data?.similarProducts) {
      data.similarProducts = data.similarProducts.filter(brandMatch);
    }
    if (data?.relatedProducts) {
      data.relatedProducts = data.relatedProducts.filter(brandMatch);
    }
  }

  return data;
};
